import { useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import axios from 'axios';
import { Currency } from '~/components/Currency/Currency.hooks';

// NOTE: UPCOMING status is in BE but FE will not receive it. CLOSED status is not included in history api.
export type ChallengeEventWeekStatus = 'END' | 'IN_PROGRESS' | 'CLOSED';
export type ChallengeEventRewardStatus =
  | 'UNREGISTERED_WALLET'
  | 'EXPIRED'
  | 'INSUFFICIENT_REWARD'
  | 'IMPOSSIBLE'
  | 'SUCCESS'
  | 'STANDBY'
  | 'REJECTED';
export type ChallengeEventParticipateStatus = 'PARTICIPATING' | 'PARTICIPATED' | 'NOT_PARTICIPATED';
export type ChallengeEvent = {
  week: number;
  startAt: Date;
  endAt: Date;
  weekStatus: ChallengeEventWeekStatus;
  participateStatus: ChallengeEventParticipateStatus;
};

export type ChallengeEventsResponse = {
  isLast: boolean;
  pageNo: number;
  weeks: ChallengeEvent[];
};

export type ChallengeEventsParams = {
  pageNo?: number;
  pageSize?: number;
};

export type ChallengeEventHistory = Omit<ChallengeEvent, 'participateStatus'> & {
  rewardStatus: ChallengeEventRewardStatus;
  score: number;
  scoreUpScore: number;
  purchaseRewardScore: number;
  reward: number;
  rewardCurrency: Currency;
};

export type ChallengeEventHistoriesParams = ChallengeEventsParams;

export type ChallengeEventHistoriesResponse = {
  activeWeek: number;
  maxReward: number;
  maxRewardCurrency: Currency;
  expectedReward: number;
  expectedRewardCurrency: Currency;
  histories: ChallengeEventHistory[];
  last: boolean;
  pageNo: number;
};

export const transformChallengeEventHistoriesResponse = (
  data: ChallengeEventHistoriesResponse
) => ({
  ...data,
  histories: data.histories.map((each) => ({
    ...each,
    startAt: new Date(each.startAt),
    endAt: new Date(each.endAt),
  })),
});

export const challengeEventHistoriesUrl = '/api/citizen/v1/challenge/history';

export const getChallengeEventHistories = async (params: ChallengeEventHistoriesParams) => {
  const { data } = await axios.get<ChallengeEventHistoriesResponse>(challengeEventHistoriesUrl, {
    params,
  });
  return transformChallengeEventHistoriesResponse(data);
};

export const useChallengeEventHistories = (
  options?: UseInfiniteQueryOptions<ChallengeEventHistoriesResponse>
) =>
  useInfiniteQuery<ChallengeEventHistoriesResponse>({
    queryKey: ['challenge', 'event', 'history'],
    queryFn: ({ pageParam: page }) => getChallengeEventHistories({ pageNo: page, pageSize: 30 }),
    getNextPageParam: ({ pageNo, last }) => (last ? undefined : pageNo + 1),
    getPreviousPageParam: ({ pageNo }) => (pageNo < 1 ? undefined : pageNo - 1),
    ...options,
  });

export const challengeEventsUrl = '/api/citizen/v1/challenge/weeks';
export const getChallengeEvents = async (params?: ChallengeEventsParams) => {
  const { data } = await axios.get<ChallengeEventsResponse>(challengeEventsUrl, { params });
  return {
    ...data,
    weeks: data.weeks.map((each) => ({
      ...each,
      startAt: new Date(each.startAt),
      endAt: new Date(each.endAt),
    })),
  };
};
export const useChallengeEvents = (options?: UseInfiniteQueryOptions<ChallengeEventsResponse>) =>
  useInfiniteQuery<ChallengeEventsResponse>({
    queryKey: ['challenge', 'event'],
    queryFn: ({ pageParam: pageNo }) => getChallengeEvents({ pageNo }),
    getNextPageParam: (page) => (page?.isLast ? undefined : page?.pageNo + 1),
    getPreviousPageParam: (page) => (page?.pageNo < 1 ? undefined : page?.pageNo - 1),
    initialData: {
      pages: [],
      pageParams: [],
    },
    ...options,
  });
